import { Node, Edge } from '@xyflow/react';
import { AutomationRule } from '../../rules.types';
import { ENodeType, RuleMethod } from '../automations.types';
import { getConditionData, getEffectData } from './labelGenerators';
import { isString } from 'lib/typeguards';

export const position = { x: 0, y: 0 };
export const edgeType: string | undefined = 'simplebezier';

export function isSupportedRuleEffect(effect: AutomationRule['effect']): boolean {
    return (
        ((effect.method === RuleMethod.AddMemberToSegment ||
            effect.method === RuleMethod.RemoveMemberFromSegment) &&
            'impact' in effect &&
            'segmentId' in effect.impact.parameters) ||
        ((effect.method === RuleMethod.GrantPerkFromImpact ||
            effect.method === RuleMethod.RevokeFromImpact) &&
            'impact' in effect &&
            'perkId' in effect.impact.parameters) ||
        (effect.method === RuleMethod.CreateScheduledTask &&
            'impact' in effect &&
            'type' in effect.impact.parameters &&
            effect.impact.parameters.type === 'CREATE_PUSH_NOTIFICATION')
    );
}

// FIXME: we shouldn't do that, it's just to test
export function isRelatedRuleCondition(condition: any, parentId: string): boolean {
    if (!condition?.object) {
        return false;
    }
    if (Array.isArray(condition.object)) {
        return condition.object.some((item: string | { v: string }) =>
            !isString(item) ? item.v === parentId : item === parentId
        );
    }
    return !isString(condition.object) ? condition.object.v === parentId : condition.object === parentId;
}

export const pushRuleConditions = (
    nodes: Node[],
    edges: Edge[],
    effectRules: AutomationRule[],
    prefix: string,
    parentId?: string,
    layer?: 0 | 1
) => {
    effectRules.forEach(rule => {
        const conditionId = `c-${rule._id}`;
        const effectId = `${prefix}-${rule._id}`;
        if (rule.conditions.length === 1) {
            const [condition] = rule.conditions;
            nodes.push({
                id: conditionId,
                data: {
                    ...getConditionData(condition),
                    layer
                },
                position,
                type: ENodeType.Condition
            });
            edges.push({
                id: `e-${rule._id}`,
                source: conditionId,
                target: effectId,
                type: edgeType
            });
            if (isString(parentId) && isRelatedRuleCondition(condition, parentId)) {
                edges.push({
                    id: `e-${rule._id}-${parentId}`,
                    source: parentId,
                    target: `e-${rule._id}-${parentId}`,
                    type: edgeType
                });
            }
        } else if (rule.conditions.length > 1) {
            nodes.push({
                id: conditionId,
                data: {
                    label: '&',
                    layer
                },
                position,
                type: ENodeType.And
            });
            edges.push({
                id: `e-${rule._id}`,
                source: conditionId,
                target: effectId,
                type: edgeType
            });
            rule.conditions.forEach((condition, index) => {
                const subConditionId = `c-${rule._id}-${index}`;
                nodes.push({
                    id: subConditionId,
                    data: {
                        ...getConditionData(condition),
                        layer
                    },
                    position,
                    type: ENodeType.Condition
                });
                edges.push({
                    id: `e-${rule._id}-${index}`,
                    source: subConditionId,
                    target: conditionId,
                    type: edgeType
                });
                if (isString(parentId) && isRelatedRuleCondition(condition, parentId)) {
                    edges.push({
                        id: `e-${rule._id}-${index}-${parentId}`,
                        source: parentId,
                        target: subConditionId,
                        type: edgeType
                    });
                }
            });
        }
    });
};

export const mapMatchedConditionsRules = (
    matchedConditions: AutomationRule[],
    nodes: Node[],
    edges: Edge[],
    coreId: string
) => {
    if (matchedConditions.length > 0) {
        matchedConditions.forEach(rule => {
            if (isSupportedRuleEffect(rule.effect)) {
                nodes.push({
                    id: `match-c-effect-${rule._id}`,
                    data: getEffectData(rule.effect),
                    position,
                    type: ENodeType.Effect
                });
                pushRuleConditions(nodes, edges, [rule], 'match-c-effect', coreId, 1);
            } else {
                nodes.push({
                    id: `match-c-${rule._id}`,
                    data: {
                        label: 'Unimplemented rule',
                        error: 'This rule effect type is not supported in the visualization.',
                        layer: 1
                    },
                    position,
                    type: ENodeType.Rule
                });
                edges.push({
                    id: `e-match-c-${rule._id}`,
                    source: coreId,
                    target: `match-c-${rule._id}`,
                    type: edgeType
                });
            }
        });
    }
};

export const getGraphFromPerkRules = (
    matchedEffects: AutomationRule[],
    matchedConditions: AutomationRule[],
    coreId = 'core',
    coreTitle = 'Results'
): [Node[], Edge[]] => {
    const grantPerkRules = matchedEffects.filter(
        rule => rule.effect.method === RuleMethod.GrantPerkFromImpact
    );
    const revokePerkRules = matchedEffects.filter(rule => rule.effect.method === RuleMethod.RevokeFromImpact);
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // main node (perk)
    nodes.push({
        id: coreId,
        data: {
            label: coreTitle
        },
        position,
        type: ENodeType.Core
    });

    grantPerkRules.forEach(rule => {
        nodes.push({
            id: `grantPerk-${rule._id}`,
            data: {
                ...getEffectData(rule.effect),
                title: 'The following perk will be granted'
            },
            position,
            type: ENodeType.Effect
        });
        edges.push({
            id: `e-grantPerk-${rule._id}`,
            source: `grantPerk-${rule._id}`,
            target: coreId,
            type: edgeType
        });
    });
    revokePerkRules.forEach(rule => {
        nodes.push({
            id: `revokePerk-${rule._id}`,
            data: {
                ...getEffectData(rule.effect),
                title: 'The following perk will be revoked'
            },
            position,
            type: ENodeType.Effect
        });
        edges.push({
            id: `e-revokePerk-${rule._id}`,
            source: `revokePerk-${rule._id}`,
            target: coreId,
            type: edgeType
        });
    });
    pushRuleConditions(nodes, edges, grantPerkRules, 'grantPerk');
    pushRuleConditions(nodes, edges, revokePerkRules, 'revokePerk');

    mapMatchedConditionsRules(matchedConditions, nodes, edges, coreId);

    return [nodes, edges];
};

export const getGraphFromAudienceRules = (
    matchedEffects: AutomationRule[],
    matchedConditions: AutomationRule[],
    coreId = 'core',
    coreTitle = 'Results'
): [Node[], Edge[]] => {
    const addMemberRules = matchedEffects.filter(
        rule => rule.effect.method === RuleMethod.AddMemberToSegment
    );
    const removeMemberRules = matchedEffects.filter(
        rule => rule.effect.method === RuleMethod.RemoveMemberFromSegment
    );
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    nodes.push({
        id: coreId,
        data: {
            label: coreTitle
        },
        position,
        type: ENodeType.Core
    });

    addMemberRules.forEach(rule => {
        nodes.push({
            id: `addMember-${rule._id}`,
            data: {
                ...getEffectData(rule.effect),
                title: 'The user will be added to'
            },
            position,
            type: ENodeType.Effect
        });
        edges.push({
            id: `e-addMember-${rule._id}`,
            source: `addMember-${rule._id}`,
            target: coreId,
            type: edgeType
        });
    });
    removeMemberRules.forEach(rule => {
        nodes.push({
            id: `removeMember-${rule._id}`,
            data: {
                ...getEffectData(rule.effect),
                title: 'The user will be removed from'
            },
            position,
            type: ENodeType.Effect
        });
        edges.push({
            id: `e-removeMember-${rule._id}`,
            source: `removeMember-${rule._id}`,
            target: coreId,
            type: edgeType
        });
    });
    pushRuleConditions(nodes, edges, addMemberRules, 'addMember');
    pushRuleConditions(nodes, edges, removeMemberRules, 'removeMember');

    mapMatchedConditionsRules(matchedConditions, nodes, edges, coreId);

    return [nodes, edges];
};
